// project imports
// action - state management
import * as actionTypes from "../../actions/actionTypes";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import {
  FOUNDERS_AND_PARTNERS_REDUCER_KEY,
  HIRING_PLAN_AVERAGE_REDUCER_KEY,
  HIRING_PLAN_OTHER_REDUCER_KEY,
  HP_FINANCE_AND_ANALYTICS_REDUCER_KEY,
  SALES_AND_MARKETING_REDUCER_KEY,
  TECH_AND_PRODUCT_DEV_REDUCER_KEY,
} from "..";
import {
  HEAD_COUNT_KEY as FINANCE_ANALYTICS_HEAD_COUNT_KEY,
  FINANCE_AND_ANALYTICS_HEADER_KEY as FINANCE_AND_ANALYTICS_TOTAL_COST,
} from "./financeAndAnalyticsReducer";
import {
  HEAD_COUNT_KEY as FOUNDERS_AND_PARTNERS_HEAD_COUNT_KEY,
  FOUNDERS_AND_PARTNERS_HEADER_KEY as FOUNDERS_AND_PARTNERS_TOTAL_COST,
} from "./foundersAndPartnersReducer";
import {
  HEAD_COUNT_KEY as OTHER_HEAD_COUNT_KEY,
  OTHER_HEADER_KEY as OTHER_TOTAL_COST,
} from "./hiringPlanOtherReducer";
import {
  HEAD_COUNT_KEY as SALES_AND_MARKETING_HEAD_COUNT_KEY,
  SALES_AND_MARKETING_HEADER_KEY,
  SALES_AND_MARKETING_HEADER_KEY as SALES_AND_MARKETING_TOTAL_COST,
} from "./salesAndMarketingReducer";
import {
  HEAD_COUNT_KEY as TECH_AND_PRODUCT_HEAD_COUNT_KEY,
  TECH_AND_PRODUCT_DEV_HEADER_KEY as TECH_PRODUCT_DEV_TOTAL_COST,
} from "./techProductDevReducer";
import { hp_fp_foundersAndPartnersHeaderMonth } from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

const prefix = "hp_avg_";

export const TOTAL_HEAD_COUNT_KEY = prefix + "totalHeadCount";
export const TOTAL_COST_KEY = prefix + "totalCost";
export const AVERAGE_SALARY_KEY = prefix + "avgSalary";
export const ADMIN_EXPENSES_KEY = prefix + "genAdminExpenses";
export const SALES_AND_MARKETING_EXPENSES_KEY =
  prefix + "salesAndMarketingExpenses";

export const hiringPlanAverageItems = [
  TOTAL_HEAD_COUNT_KEY,
  TOTAL_COST_KEY,
  AVERAGE_SALARY_KEY,
  ADMIN_EXPENSES_KEY,
  SALES_AND_MARKETING_EXPENSES_KEY,
];

export const initialState = {
  [TOTAL_HEAD_COUNT_KEY]: {
    title: "Total Head Count",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return (
        Number(
          getReducerFunc(HP_FINANCE_AND_ANALYTICS_REDUCER_KEY)[
            FINANCE_ANALYTICS_HEAD_COUNT_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(FOUNDERS_AND_PARTNERS_REDUCER_KEY)[
            FOUNDERS_AND_PARTNERS_HEAD_COUNT_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(HIRING_PLAN_OTHER_REDUCER_KEY)[
            OTHER_HEAD_COUNT_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(SALES_AND_MARKETING_REDUCER_KEY)[
            SALES_AND_MARKETING_HEAD_COUNT_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(TECH_AND_PRODUCT_DEV_REDUCER_KEY)[
            TECH_AND_PRODUCT_HEAD_COUNT_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        )
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_COST_KEY]: {
    title: "Total Cost",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return (
        Number(
          getReducerFunc(HP_FINANCE_AND_ANALYTICS_REDUCER_KEY)[
            FINANCE_AND_ANALYTICS_TOTAL_COST
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(FOUNDERS_AND_PARTNERS_REDUCER_KEY)[
            FOUNDERS_AND_PARTNERS_TOTAL_COST
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(HIRING_PLAN_OTHER_REDUCER_KEY)[
            OTHER_TOTAL_COST
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(SALES_AND_MARKETING_REDUCER_KEY)[
            SALES_AND_MARKETING_TOTAL_COST
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(TECH_AND_PRODUCT_DEV_REDUCER_KEY)[
            TECH_PRODUCT_DEV_TOTAL_COST
          ].calculate(yearKey, getReducerFunc, companyType)
        )
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [AVERAGE_SALARY_KEY]: {
    title: "Average Salary per Head Count",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const totalCost = Number(
        getReducerFunc(HIRING_PLAN_AVERAGE_REDUCER_KEY)[TOTAL_COST_KEY].calculate(
          yearKey,
          getReducerFunc,
          companyType
        )
      );
      const totalHeadCount = Number(
        getReducerFunc(HIRING_PLAN_AVERAGE_REDUCER_KEY)[
          TOTAL_HEAD_COUNT_KEY
        ].calculate(yearKey, getReducerFunc, companyType)
      );

      if (!totalHeadCount) {
        return 0;
      }

      const result = totalCost / totalHeadCount;
      return Number.isFinite(result) ? Number(result.toFixed(0)) : 0;
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [ADMIN_EXPENSES_KEY]: {
    title: "General Administrative Expenses",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return (
        Number(
          getReducerFunc(HIRING_PLAN_AVERAGE_REDUCER_KEY)[
            TOTAL_COST_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) -
        Number(
          getReducerFunc(HIRING_PLAN_AVERAGE_REDUCER_KEY)[
            SALES_AND_MARKETING_EXPENSES_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        )
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SALES_AND_MARKETING_EXPENSES_KEY]: {
    title: "Sales and Marketing Expenses",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return Number(
        getReducerFunc(SALES_AND_MARKETING_REDUCER_KEY)[
          SALES_AND_MARKETING_HEADER_KEY
        ].calculate(yearKey, getReducerFunc, companyType)
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const hiringPlanAverageReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_HIRING_PLAN_AVERAGE_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_HIRING_PLAN_AVERAGE_BY_MONTHS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_HIRING_PLAN_AVERAGE_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_HIRING_PLAN_AVERAGE_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default hiringPlanAverageReducer;
